import React, { useState, useEffect } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import ImageLightbox from "../components/ImageLightbox";
import { fetchIssues, updateIssueStatus, uploadIssuePhoto } from "../api/api";

function formatDateTime(iso) {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const STATUS_LABELS = {
  received: "Received",
  review: "Review",
  resolved: "Resolved",
  closed: "Closed",
};

const STATUS_CLASS = {
  received: "status-open",
  review: "status-progress",
  resolved: "status-progress",
  closed: "status-closed",
};

const MAX_PHOTOS = 5;

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

function Issues() {

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [updating, setUpdating] = useState(false);

  const [resolutionNotes, setResolutionNotes] = useState("");
  const [pendingPhotoUrls, setPendingPhotoUrls] = useState([]);
  const [pendingFileUrls, setPendingFileUrls] = useState([]);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const loadRequests = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchIssues();
      setRequests(data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const filteredRequests = statusFilter
    ? requests.filter((r) => r.status === statusFilter)
    : requests;

  const countByStatus = (status) => requests.filter((r) => r.status === status).length;

  const selectRequest = (req) => {
    setSelectedRequest(req);
    setResolutionNotes("");
    setPendingPhotoUrls([]);
    setPendingFileUrls([]);
  };

  const changeStatus = async (issueId, newStatus) => {
    if (!resolutionNotes.trim()) {
      setError("Please add a message explaining this update before saving.");
      return;
    }
    setUpdating(true);
    setError("");
    try {
      const updated = await updateIssueStatus(issueId, newStatus, resolutionNotes.trim(), pendingPhotoUrls, pendingFileUrls);
      setRequests((prev) => prev.map((r) => (r.id === issueId ? updated : r)));
      setSelectedRequest(updated);
      setResolutionNotes("");
      setPendingPhotoUrls([]);
      setPendingFileUrls([]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUpdating(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selectedRequest) return;

    if (pendingPhotoUrls.length >= MAX_PHOTOS) {
      setError(`Maximum ${MAX_PHOTOS} photos per status change.`);
      return;
    }

    setUploadingPhoto(true);
    setError("");
    try {
      const file_url = await uploadIssuePhoto(file, selectedRequest.id);
      setPendingPhotoUrls((prev) => [...prev, file_url]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selectedRequest) return;

    if (pendingFileUrls.length >= MAX_PHOTOS) {
      setError(`Maximum ${MAX_PHOTOS} files per status change.`);
      return;
    }

    setUploadingFile(true);
    setError("");
    try {
      const file_url = await uploadIssuePhoto(file, selectedRequest.id);
      setPendingFileUrls((prev) => [...prev, file_url]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUploadingFile(false);
    }
  };

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        {/* TOP HEADER */}

        <div className="service-header">

          <h1 style={{ marginTop: 0, marginBottom: 0 }}>
            Issues
          </h1>

          <div className="service-count">
            {requests.length}
          </div>

        </div>

        {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}

        {/* FILTER BUTTONS */}

        <div className="filter-row">

          <button className={statusFilter === "" ? "active-filter" : ""} onClick={() => setStatusFilter("")}>
            All ({requests.length})
          </button>

          <button className={statusFilter === "received" ? "active-filter" : ""} onClick={() => setStatusFilter("received")}>
            Received ({countByStatus("received")})
          </button>

          <button className={statusFilter === "review" ? "active-filter" : ""} onClick={() => setStatusFilter("review")}>
            Review ({countByStatus("review")})
          </button>

          <button className={statusFilter === "resolved" ? "active-filter" : ""} onClick={() => setStatusFilter("resolved")}>
            Resolved ({countByStatus("resolved")})
          </button>

          <button className={statusFilter === "closed" ? "active-filter" : ""} onClick={() => setStatusFilter("closed")}>
            Closed ({countByStatus("closed")})
          </button>

        </div>

        {loading && <p>Loading tickets…</p>}

        {!loading && filteredRequests.length === 0 && <p>No tickets match this filter.</p>}

        {/* TABLE */}

        {!loading && filteredRequests.length > 0 && (
          <div className="table-card">

            <table className="service-table">

              <thead>

                <tr>

                  <th>#</th>

                  <th>Category</th>

                  <th>Title</th>

                  <th>Plot / Owner</th>

                  <th>Raised</th>

                  <th>Status</th>

                  <th>Action</th>

                </tr>

              </thead>

              <tbody>

                {
                  filteredRequests.map((req) => (

                    <tr key={req.id}>

                      <td>{req.ticket_number}</td>

                      <td>{req.category_name || "—"}</td>

                      <td>{req.title}</td>

                      <td>{req.plot_number ? `${req.plot_number} • ${req.owner_name || "—"}` : "—"}</td>

                      <td>{new Date(req.created_at).toLocaleDateString()}</td>

                      <td>

                        <span className={STATUS_CLASS[req.status]}>
                          {STATUS_LABELS[req.status]}
                        </span>

                      </td>

                      <td>

                        <button
                          className="view-btn"
                          onClick={() => selectRequest(req)}
                        >

                          {req.status === "received" ? "Respond" : "View"}

                        </button>

                      </td>

                    </tr>

                  ))
                }

              </tbody>

            </table>

          </div>
        )}


        {/* DETAILS CARD */}

        {
          selectedRequest && (

            <div className="request-details">

              <div className="details-top">

                <h3>
                  {selectedRequest.ticket_number} — {selectedRequest.title}
                </h3>

                <span className={STATUS_CLASS[selectedRequest.status]}>
                  {STATUS_LABELS[selectedRequest.status]}
                </span>

              </div>

              <p className="details-owner">
                Plot {selectedRequest.plot_number || "—"} • {selectedRequest.owner_name || "—"} • Raised {new Date(selectedRequest.created_at).toLocaleDateString()}
              </p>

              <p className="details-message">
                "{selectedRequest.description}"
              </p>

              {selectedRequest.status === "closed" ? (
                <p style={{ color: "#666", marginTop: "12px" }}>
                  This issue has been closed by the customer — see the history below.
                </p>
              ) : (
                <>
                  <label style={{ display: "block", fontWeight: 600, marginTop: "12px", marginBottom: "6px" }}>
                    Message for the customer <span style={{ color: "red" }}>*</span>
                  </label>
                  <textarea
                    rows="3"
                    style={{ width: "100%" }}
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder="Explain what changed…"
                  />

                  <div style={{ marginTop: "12px" }}>
                    <label style={{ fontWeight: 600 }}>
                      Photos ({pendingPhotoUrls.length}/{MAX_PHOTOS}) — optional
                    </label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "6px" }}>
                      {pendingPhotoUrls.map((url) => (
                        <img
                          key={url} src={url} alt="Newly uploaded"
                          onClick={() => setViewerUrl(url)}
                          style={{ width: "72px", height: "72px", objectFit: "cover", borderRadius: "6px", border: "2px solid #2e7d32", cursor: "pointer" }}
                        />
                      ))}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhoto || pendingPhotoUrls.length >= MAX_PHOTOS}
                      style={{ marginTop: "8px" }}
                    />
                  </div>

                  <div style={{ marginTop: "12px" }}>
                    <label style={{ fontWeight: 600 }}>
                      Files ({pendingFileUrls.length}/{MAX_PHOTOS}) — optional
                    </label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "6px" }}>
                      {pendingFileUrls.map((url) => (
                        <a key={url} href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "13px" }}>📎 {url.split("/").pop()}</a>
                      ))}
                    </div>
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      disabled={uploadingFile || pendingFileUrls.length >= MAX_PHOTOS}
                      style={{ marginTop: "8px" }}
                    />
                  </div>

                  {/* BUTTONS */}

                  <div className="response-actions" style={{ marginTop: "16px" }}>

                    <button
                      className="progress-btn"
                      disabled={updating || selectedRequest.status === "review"}
                      onClick={() => changeStatus(selectedRequest.id, "review")}
                    >
                      {updating ? "Updating…" : "Mark In Review"}
                    </button>

                    <button
                      className="progress-btn"
                      disabled={updating || selectedRequest.status === "resolved"}
                      onClick={() => changeStatus(selectedRequest.id, "resolved")}
                    >
                      {updating ? "Updating…" : "Mark Resolved"}
                    </button>

                  </div>
                </>
              )}

              {/* STATUS HISTORY */}

              {selectedRequest.status_history?.length > 0 && (
                <div style={{ marginTop: "20px", borderTop: "1px solid #eee", paddingTop: "12px" }}>
                  <h4 style={{ marginBottom: "10px" }}>History</h4>
                  {selectedRequest.status_history.map((h) => (
                    <div key={h.id} style={{ marginBottom: "12px", paddingBottom: "12px", borderBottom: "1px solid #f5f5f5" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span className={STATUS_CLASS[h.status]}>{STATUS_LABELS[h.status]}</span>
                        <span style={{ fontSize: "12px", color: "#888" }}>{formatDateTime(h.created_at)}</span>
                      </div>
                      {h.changed_by_name && (
                        <p style={{ fontSize: "13px", color: "#555", margin: "4px 0 0" }}>by {h.changed_by_name}</p>
                      )}
                      {h.notes && <p style={{ margin: "4px 0 0" }}>{h.notes}</p>}
                      {h.rating && (
                        <p style={{ margin: "4px 0 0", color: "#f59e0b" }}>{"★".repeat(h.rating)}{"☆".repeat(5 - h.rating)}</p>
                      )}
                      {h.attachments?.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "6px" }}>
                          {h.attachments.map((a) => (
                            a.kind === "photo" ? (
                              <img
                                key={a.id} src={a.file_url} alt="Attachment"
                                onClick={() => setViewerUrl(a.file_url)}
                                style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "6px", cursor: "pointer" }}
                              />
                            ) : (
                              <a key={a.id} href={a.file_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "13px" }}>📎 {a.file_url.split("/").pop()}</a>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

            </div>

          )
        }

      </div>

      <ImageLightbox url={viewerUrl} onClose={() => setViewerUrl(null)} />

    </div>

  );

}

export default Issues;
